import { AfterViewInit, Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { sha256 } from 'js-sha256';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTableModule } from 'ng-zorro-antd/table';
import { Subject, takeUntil } from 'rxjs';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { ProfileDto } from 'src/app/shared/dto/profile.dto';
import { UserDto } from 'src/app/shared/dto/user.dto';
import { isEmail } from 'src/app/shared/utils/utils';
import { UserService } from '../../services/user.service';
import { NzFormDirective, NzFormItemComponent, NzFormControlComponent, NzFormModule } from 'ng-zorro-antd/form';
import { NzRowDirective, NzColDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { DrawerMode } from 'src/app/core/enums/drawer-mode';
import { DRAWER_WIDTH_CHANGE_PLACEMENT } from 'src/app/core/constants/components';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { DefaultComponent } from 'src/app/shared/services/view.service';
import { NzButtonModule } from 'ng-zorro-antd/button';

@Component({
    selector: 'app-users',
    standalone: true,
    imports: [
        NzPageHeaderModule,
        NzSpaceModule,
        NzDividerModule,
        NzDrawerModule,
        NzDatePickerModule,
        FormsModule,
        NzMessageModule,
        NzTableModule,
        NzSelectModule,
        NzInputDirective,
        ReactiveFormsModule,
        NzFormDirective,
        NzRowDirective,
        NzFormItemComponent,
        NzColDirective,
        NzFormControlComponent,
        NzInputDirective,
        NzFormModule,
        NzCheckboxModule,
        NzDropDownModule,
        NzIconModule,
        NzToolTipModule,
        LoaderComponent,
        NzButtonModule
    ],
    templateUrl: './users.component.html',
    styleUrl: './users.component.scss'
})
export class UsersComponent extends DefaultComponent implements OnInit, AfterViewInit, OnDestroy {

    users: UserDto[] = [];
    profileDataSet: ProfileDto[] = [new ProfileDto()];
    fieldsReadOnly: boolean = true;
    drawerTitle: string = 'Placeholder';
    drawerMode!: DrawerMode;
    drawerVisible: boolean = false;
    userSelected: UserDto = new UserDto();
    profileSelected: ProfileDto = new ProfileDto();
    externalAccess: boolean = false;
    codeExpirationDate: Date = new Date();
    currentUser: UserDto = new UserDto();
    deleteMe: string = 'deleteMe';
    isLoading: boolean = true;
    isSaving: boolean = false;

    innerWidth: number = 0;
    DRAWER_WIDTH_CHANGE_PLACEMENT = DRAWER_WIDTH_CHANGE_PLACEMENT;

    validateForm: FormGroup<{
        name: FormControl<string>;
        surname: FormControl<string>;
        username: FormControl<string>;
        password: FormControl<string>;
        email: FormControl<string>;
        rfid: FormControl<string>;
        externalAccess: FormControl<boolean>;
        profile: FormControl<ProfileDto>;
    }>;

    private readonly userService = inject(UserService);
    private readonly message = inject(NzMessageService);
    private readonly userLoggedService = inject(UserLoggedService);
    private readonly fb = inject(NonNullableFormBuilder);

    private unsubscribe$ = new Subject<void>();

    constructor() {
        super('users', '');
        this.validateForm = this.fb.group({
            name: ['', [Validators.required]],
            surname: ['', [Validators.required]],
            username: ['', [Validators.required]],
            password: [''],
            email: ['', [Validators.required, Validators.email]],
            rfid: [''],
            externalAccess: [false],
            profile: [new ProfileDto()]
        });
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.innerWidth = window.innerWidth;
    }

    async ngOnInit() {
        this.currentUser = this.userLoggedService.userLogged;
        this.innerWidth = window.innerWidth;

        await this.userService.setupWebSocket();
        this.userService.users$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: async (users) => {
                    this.users = users;
                    if (this.users == undefined || this.users.length === 0) {
                        this.message.error('La lista de usuarios está vacía');
                    }

                    this.profileDataSet = await this.userService.getAssignableProfiles();

                    if (this.profileDataSet == undefined || this.profileDataSet.length === 0) {
                        this.message.error('La lista de perfiles está vacía');
                    }

                    this.userSelected = this.users.find(user => user.id === this.userSelected?.id) as UserDto;
                    if (this.userSelected) {
                        this.externalAccess = this.userSelected?.externalAccess || false;
                        this.profileSelected = this.userSelected?.profile || new ProfileDto();
                    }
                },
                error: () => {
                    this.message.error('Error cargando usuarios');
                }
            });

        this.isLoading = false;
    }

    ngAfterViewInit() {
        this.innerWidth = window.innerWidth;
    }

    ngOnDestroy() {
        this.userService.disconnectWebSocket();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    create() {
        this.userSelected = new UserDto();
        this.drawerMode = DrawerMode.CREATE;
        this.drawerTitle = 'Creando nuevo usuario';
        this.fieldsReadOnly = false;
        this.validateForm.controls.password.setValidators([Validators.required]);
        this.validateForm.enable();
        this.openDrawer();
    }

    view(data: UserDto) {
        this.validateForm.patchValue(data);
        this.validateForm.disable();
        this.userSelected = data;
        this.drawerMode = DrawerMode.VIEW;
        this.drawerTitle = 'Viendo a usuario ' + this.userSelected?.username;
        this.fieldsReadOnly = true;
        this.setData();
        this.openDrawer();
    }

    edit(data: UserDto) {
        this.userSelected = data;
        this.validateForm.patchValue(data);
        this.validateForm.enable();
        this.drawerMode = DrawerMode.EDIT;
        this.drawerTitle = 'Editando a usuario ' + this.userSelected?.username;
        this.fieldsReadOnly = false;
        this.validateForm.controls.password.clearValidators();
        this.setData();
        this.openDrawer();
    }

    setData() {
        this.externalAccess = this.userSelected.externalAccess;
        this.codeExpirationDate = this.userSelected.codeExpirationDate
            ? new Date(this.userSelected.codeExpirationDate)
            : new Date();
        this.profileSelected = this.profileDataSet.find(
            (p) => p.id === this.userSelected.profile?.id,
        ) as ProfileDto;
    }

    async delete(uid: number) {
        try {
            if (window.confirm('¿Estás seguro de que quieres eliminar este usuario? Esta acción no se puede deshacer.')) {
                const deletedUser: UserDto = await this.userService.deleteUser(uid);

                if (deletedUser == undefined) {
                    throw new Error('User not found');
                }

                const index = this.users.findIndex((u) => u.id === uid);

                if (index < 0) {
                    this.message.create('error', `No se ha podido eliminar el usuario ${deletedUser.name} con id ${uid}, usuario no encontrado`);
                    throw new Error('User not found');
                }

                this.users.splice(index, 1);
                const data = [...this.users];
                this.users = data;
                this.message.create('success', 'Usuario eliminado correctamente',);
            } else {
                console.log('Cancelando eliminación de usuario ' + uid);
            }
        } catch (e: any) {
            this.message.create('error', `No se ha podido eliminar el usuario. Error: ${e.message}`);
        }
    }

    async submitForm() {
        try {
            this.isSaving = true;

            const pass: string = this.validateForm.controls.password.value ? sha256(this.validateForm.controls.password.value) : this.deleteMe;
            const customUserDto: UserDto = {
                id: this.userSelected.id,
                name: this.validateForm.controls.name.value ? this.validateForm.controls.name.value : '',
                firstname: this.validateForm.controls.name.value ? this.validateForm.controls.name.value : '',
                surname: this.validateForm.controls.surname.value ? this.validateForm.controls.surname.value : '',
                username: this.validateForm.controls.username.value ? this.validateForm.controls.username.value : '',
                rfid: this.validateForm.controls.rfid.value ? this.validateForm.controls.rfid.value : '',
                email: this.validateForm.controls.email.value ? this.validateForm.controls.email.value : '',
                createdBy: this.drawerMode === DrawerMode.CREATE ? this.currentUser.id : this.userSelected.createdBy,
                updatedBy: this.currentUser.id,
                externalAccess: this.validateForm.controls.externalAccess.value ? this.validateForm.controls.externalAccess.value : false,
                code: (<HTMLInputElement>document.getElementById('code'))?.value
                    ? ((<HTMLInputElement>document.getElementById('code'))
                        ?.value as unknown as number)
                    : 0,
                codeExpirationDate: this.codeExpirationDate,
                password: pass,
                profileId: this.validateForm.controls.profile.value ? this.validateForm.controls.profile.value.id : 0,
                profile: this.validateForm.controls.profile.value ? this.validateForm.controls.profile.value : new ProfileDto()
            };

            if (customUserDto.password == this.deleteMe) {
                delete customUserDto.password;
            }

            if (!this.validForm(customUserDto)) {
                this.isSaving = false;
                return;
            }

            if (this.drawerMode === DrawerMode.CREATE) {
                delete customUserDto.profile;

                const createdUser: UserDto = await this.userService.createUser(customUserDto);

                if (createdUser == undefined) {
                    this.isSaving = false;
                    this.message.create('error', 'Usuario no encontrado');

                    return;
                }

                createdUser.profile = this.profileSelected;

                this.users.push(createdUser);
                const data = [...this.users];
                this.users = data;

                this.closeDrawer();

                this.isSaving = false;
                this.message.create('success', 'Usuario creado correctamente');
            } else if (this.drawerMode === DrawerMode.EDIT) {
                const updatedUser: UserDto = await this.userService.updateUser(customUserDto);

                if (updatedUser == undefined) {
                    this.isSaving = false;
                    throw new Error('Error updating user');
                }

                updatedUser.profile = this.profileSelected;

                const index = this.users.findIndex(
                    (u) => u.id === updatedUser.id
                );

                if (index < 0) {
                    this.isSaving = false;
                    this.message.create('error', `No se ha podido actualizar el usuario ${customUserDto.name} con id ' + customUserDto.id + ', usuario no encontrado`);
                    return;
                }

                this.users[index] = updatedUser;

                const data = [...this.users];
                this.users = data;

                this.closeDrawer();
                this.isSaving = false;
                this.message.create('success', 'Usuario actualizado correctamente');
            }
        } catch (e: any) {
            this.isSaving = false;
            this.message.create('error', `No se ha podido guardar. Error: ${e.message}`);
        }
    }

    cancelEditing() {
        this.closeDrawer();
    }

    closeDrawer() {
        this.userSelected = new UserDto();
        this.validateForm.reset();
        this.drawerVisible = false;
    }

    openDrawer() {
        this.drawerVisible = true;
    }

    validForm(user: UserDto) {
        console.log(user.name.length);
        if (user.name.length == 0) {
            this.message.create(
                'error',
                'El campo Nombre no puede estar vacío',
            );
            return false;
        }
        if (user.surname.length == 0) {
            this.message.create(
                'error',
                'El campo Apellido no puede estar vacío',
            );
            return false;
        }
        if (user.username.length == 0) {
            this.message.create(
                'error',
                'El campo Username no puede estar vacío',
            );
            return false;
        }
        if (user.password && (user.password.length == 0 && this.drawerMode === DrawerMode.CREATE)) {
            this.message.create(
                'error',
                'El campo Contraseña no puede estar vacío',
            );
            return false;
        }
        if (user.email.length == 0) {
            this.message.create('error', 'El campo Email no puede estar vacío');
            return false;
        }
        if (!isEmail(user.email)) {
            this.message.create(
                'error',
                'El campo Email no es un email válido',
            );
            return false;
        }
        return true;
    }

    compareProfiles = (o1: any, o2: any): boolean => {
        return o1 && o2 ? o1.name === o2.name : o1 === o2;
    };

    isNewUser() {
        return this.drawerMode === DrawerMode.CREATE;
    }
}
